import React, { useRef } from 'react';
import type { Identifier, XYCoord } from 'dnd-core';
import { useDrag, useDrop } from 'react-dnd';
import { BsArrowsMove } from 'react-icons/bs';

interface SortableItemProps<T> {
  renderItem: (item: T, index: number) => React.ReactNode;
  keyExtractor: (item: T, index: number) => string;
  element: T;
  index: number;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  disabled?: boolean;
}
interface DragItem {
  index: number;
  id: string;
}

export const SortableItem = <T,>({
  renderItem,
  element,
  keyExtractor,
  index,
  moveItem,
  disabled,
}: SortableItemProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: 'row',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveItem(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'row',
    item: () => {
      return { id: keyExtractor(element, index), index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  return (
    <div
      ref={!disabled ? ref : null}
      style={{ opacity }}
      data-handler-id={handlerId}
    >
      <div
        className={`p-1 flex flex-row justify-between rounded-md ${
          !disabled && 'hover:shadow-lg p-1 hover:border-gray-100'
        }`}
      >
        <div
          className={`flex flex-row flex-grow ${
            !disabled && 'cursor-pointer'
          } text-base align-middle items-center space-s-4`}
        >
          <BsArrowsMove className="h-5 w-5 text-textSecondary" />{' '}
          <div className="w-full">{renderItem(element, index)}</div>
        </div>
      </div>
    </div>
  );
};
